"use client";

import { authClient } from "@repo/auth/client";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { Button } from "@ui/components/button";
import { Spinner } from "@ui/components/spinner";
import { useState } from "react";
import { verifyOtpFn } from "~/server/auth";
import { AuthError } from "../AuthNotice";
import { CODE_LENGTH, CodeInput } from "../CodeInput";

interface SignupStepOtpProps {
	email: string;
	onVerified: () => void;
	onBack: () => void;
}

export function SignupStepOtp({
	email,
	onVerified,
	onBack,
}: SignupStepOtpProps) {
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [verifying, setVerifying] = useState(false);
	const [resending, setResending] = useState(false);
	const [resent, setResent] = useState(false);

	const verify = async (value: string) => {
		if (value.length !== CODE_LENGTH || verifying) {
			return;
		}
		setError(null);
		setVerifying(true);
		try {
			const result = await verifyOtpFn({ data: { email, otp: value } });
			if (!result.success) {
				setCode("");
				setError(
					result.error === "OTP_EXPIRED"
						? "That code has expired. Send a new one below."
						: result.error === "TOO_MANY_ATTEMPTS"
							? "Too many tries. Send a new code below."
							: "That code isn't right. Check the email and try again.",
				);
				return;
			}
			onVerified();
		} catch {
			setError("We couldn't check the code. Try again in a moment.");
		} finally {
			setVerifying(false);
		}
	};

	const resend = async () => {
		setError(null);
		setResent(false);
		setResending(true);
		try {
			const { error: resendError } =
				await authClient.emailOtp.sendVerificationOtp({
					email,
					type: "sign-in",
				});
			if (resendError) {
				setError(
					resendError.status === 429
						? "Too many codes sent. Wait a few minutes, then try again."
						: "We couldn't send a new code. Try again in a moment.",
				);
				return;
			}
			setResent(true);
		} catch {
			setError("We couldn't send a new code. Try again in a moment.");
		} finally {
			setResending(false);
		}
	};

	return (
		<div className="flex flex-col">
			<AuthHeading
				className="mb-6"
				title="Check your email"
				description={
					<>
						We sent a 6-digit code to{" "}
						<span className="font-medium text-foreground">
							{email}
						</span>
						.
					</>
				}
			/>
			<form
				className="flex flex-col gap-5"
				onSubmit={(event) => {
					event.preventDefault();
					verify(code);
				}}
			>
				<AuthError message={error} />
				<CodeInput
					value={code}
					onChange={setCode}
					onComplete={verify}
					disabled={verifying}
				/>
				<Button
					type="submit"
					size="lg"
					disabled={code.length < CODE_LENGTH || verifying}
				>
					{verifying ? <Spinner data-icon="inline-start" /> : null}
					Continue
				</Button>
				<div className="flex flex-wrap items-center justify-center gap-1 text-muted-foreground text-sm">
					<span>{resent ? "New code sent." : "No email?"}</span>
					<Button
						type="button"
						variant="link"
						size="sm"
						className="h-auto px-1"
						onClick={resend}
						disabled={resending}
					>
						{resending ? "Sending…" : "Send a new code"}
					</Button>
					<span aria-hidden>·</span>
					<Button
						type="button"
						variant="link"
						size="sm"
						className="h-auto px-1"
						onClick={onBack}
					>
						Change email
					</Button>
				</div>
			</form>
		</div>
	);
}
